import React, { useCallback, useEffect, useState } from "react";

import { CustomHeroComponent } from "@components/hero/CustomHero";
import { ContainerPreviewComponent } from "@components/user/Container";
import { BasicCarouselPreviewComponent } from "@components/carousel/basic/BasicCarouselPreviewComponent";
import { EventHeroPreviewComponent } from "@components/carousel/hero/EventHero";
import { FeaturedCarouselPreviewComponent } from "@components/carousel/featured/FeaturedCarousel";
import { CategoryCarouselPreviewComponent } from "@components/carousel/category/CategoryCarousel";
import { BannerPreviewComponent } from "@components/banner/BasicBanner";
import { PortraitCarouselPreviewComponent } from "@components/carousel/portrait/PortraitCarousel";

import { apiClient } from "@api/index";
import { useAuthStore } from "@state/auth";
import { useParams } from "react-router-dom";
import { useEditorStore } from "@state/editor";

type ComponentType = React.FC<any>;

const components: Record<string, ComponentType> = {
  Container: ContainerPreviewComponent,
  CustomHero: CustomHeroComponent,
  EventHero: EventHeroPreviewComponent,
  FeaturedCarousel: FeaturedCarouselPreviewComponent,
  CategoryCarousel: CategoryCarouselPreviewComponent,
  BasicCarousel: BasicCarouselPreviewComponent,
  BasicBanner: BannerPreviewComponent,
  PortraitCarousel: PortraitCarouselPreviewComponent,
};

const Preview = () => {
  const params = useParams();
  const user = useAuthStore((state) => state.user);
  const { setIsPreview, setRenderPanelSize, isPreview } = useEditorStore((state) => state);
  const [renderPanel, setRenderPanel] = useState<HTMLDivElement | null>(null);

  const { appID } = params;

  const { data, isLoading } = apiClient.ott.getAppByID.useQuery({
    appID: appID as string,
    clientID: user.clientID,
  });

  const setRenderPanelRef = useCallback((node: HTMLDivElement | null) => {
    setRenderPanel(node); // Update the state with the DOM node
  }, []);

  useEffect(() => {
    if (!isPreview) {
      setIsPreview(true);
    }
  }, [isPreview, setIsPreview]);

  useEffect(() => {
    if (renderPanel) {
      // Create a ResizeObserver to track changes in the element's size
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect) {
            const { width, height } = entry.contentRect;

            // Use requestAnimationFrame to reduce the frequency of updates
            requestAnimationFrame(() => {
              setRenderPanelSize({ width, height });
            });
          }
        }
      });

      resizeObserver.observe(renderPanel);

      // Cleanup the observer on component unmount
      return () => resizeObserver.disconnect();
    }
  }, [renderPanel, setRenderPanelSize, setIsPreview]);

  if (!data || isLoading) {
    return (
      <div className="h-screen w-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-l-0 border-r-2"></div>
      </div>
    );
  }

  const getComponents = (data: any) => {
    // Iterate over the data object
    for (const nodeId in data) {
      const element = data[nodeId];

      // If the element is the root node
      if (nodeId === "ROOT") {
        return (
          <ContainerPreviewComponent>
            {element.nodes.map((nodeId: string) => {
              const node = data[nodeId];

              if (!components[node?.type?.resolvedName]) return null;

              const Component = components[node?.type?.resolvedName] as React.ElementType<any>;

              return (
                <Component
                  key={nodeId}
                  id={nodeId}
                  {...node.props}
                />
              );
            })}
          </ContainerPreviewComponent>
        );
      } else {
      }
    }
  };

  return (
    <>
      {/* <head>
        <title>{data.appName} - Invintus Preview</title>
      </head> */}

      <main ref={setRenderPanelRef}>{data?.manifest.data.elements && <>{getComponents(data.manifest.data.elements)}</>}</main>
    </>
  );
};

export default Preview;
